import type { AnyAction, PayloadAction } from "@reduxjs/toolkit";
import type { EditorDispatch, EditorRootState } from "@v1/store/store";
import type { ComponentActionType } from "@v1/types/component-action-type";
import type {
  MetafieldKey,
  MetafieldNamespace,
  ProductId,
  ProductMetafieldsDependency,
  VariantId,
  VariantMetafieldsDependency,
} from "replo-runtime/shared/types";
import type {
  PreviewElementProps,
  ReploElement,
  ReploPartialElement,
  UpdateElementSource,
} from "schemas/generated/element";

import { createAsyncThunk } from "@reduxjs/toolkit";
import EditorActionType from "@v1/constants/editor-action-types";
import {
  selectLocaleData,
  selectProductData,
  selectShopifyUrlRoot,
  setProductMetafieldValues,
  setVariantsMetafieldValues,
} from "@v1/reducers/commerce-reducer";
import {
  applyComponentRTKAction,
  handleUpdateComponentError,
  handleUpdateComponentStart,
  handleUpdateComponentSuccess,
  selectDraftElementId,
  selectDraftElementVersion,
  selectProjectId,
  setDraftElement as setDraftElementAction,
  setPendingElementUpdate,
} from "@v1/reducers/core-reducer";
import { selectTemplateEditorStoreProduct } from "@v1/reducers/template-reducer";
import { setIsRichTextEditorFocused } from "@v1/reducers/ui-reducer";
import { shopifyClient } from "@v1/trpc/routes/shopify";
import {
  calculateDependencies,
  getMetafieldsNamespaceKeyTypeMapping,
  getProductsDataForDependencies,
} from "@v1/utils/dependencies";
import { trackError } from "@v2/features/infra/analytics/tracking";
import { modalStore } from "@v2/store/modalStore";
import { elementClient } from "@v2/trpc/routes/element";
import groupBy from "lodash-es/groupBy";
import mapValues from "lodash-es/mapValues";
import size from "lodash-es/size";
import uniq from "lodash-es/uniq";
import { recordValues } from "replo-runtime/shared/utils/object";
import { getFromRecordOrNull } from "replo-runtime/shared/utils/optional";

type SetDraftElementPayloadOptions = {
  minimizeLeftSidebar: boolean;
};

export type SetDraftElementPayload = {
  id?: string | null;
  componentIds?: string[];
  symbolId?: string;
  variantId?: string;
  options?: SetDraftElementPayloadOptions;
  draftNodes?: HTMLElement[];
};

export const setDraftElement = (payload: SetDraftElementPayload) => {
  const actions: (
    | PayloadAction<SetDraftElementPayload>
    | PayloadAction<string[]>
    | PayloadAction<string | null>
    | PayloadAction<boolean>
  )[] = [setDraftElementAction(payload)];

  // Note (Ovishek, 2022-05-25, REPL-2317) we are setting isRichTextEditorFocused to be "false" here, b/c rte is too dumb that
  // it's onBlur doesn't always trigger if you click on the canvas from rte editing mode. So it's safe to make
  // it false here. This solves the problem with copy/pasting after editing on rte.
  actions.push(setIsRichTextEditorFocused(false));

  return actions;
};

/**
 * Create a thunk (action) which executes a given action _then_ updates a given
 * element, or queues the element for immediate update if there's already an
 * element update in progress.
 *
 * Use this action creator any time you need to update and save an element.
 */
export const createElementUpdatingThunk = (
  action: any,
  getElementId: (getState: () => EditorRootState) => string | null,
  source: UpdateElementSource = "component",
) => {
  return (dispatch: (action: any) => any, getState: () => EditorRootState) => {
    const state = getState();
    const coreState = state.core;
    const elementId = getElementId(getState);
    const element = getFromRecordOrNull(state.core.elements.mapping, elementId);
    // NOTE (Gabe 2024-03-07): We need to check if the element is null here
    // because sometimes this fires before the element is in the redux store.
    // Without this we send an incomplete request to the backend causing zod
    // errors.
    if (!elementId || !element) {
      dispatch(action);
      return;
    }
    if (coreState.elementUpdateInProgress) {
      // If there are in progress updates, then execute the action but don't
      // make the request, just mark the element as queued
      dispatch([action, setPendingElementUpdate(elementId)]);
    } else {
      // If there are no pending requests, execute the action, set the element
      // as pending so savePendingElements knows to pop it off the queue,
      // then dispatch an savePendingElements to kick off the request
      dispatch([
        action,
        setPendingElementUpdate(elementId),
        savePendingElements(source),
      ]);
    }
  };
};

export const createFetchMetafieldValuesIfNeededThunk = createAsyncThunk(
  "metafields/fetchIfNeeded",
  async (elementId: string | null, { dispatch, getState, rejectWithValue }) => {
    const state = getState() as EditorRootState;
    const draftElementId = elementId ?? state.core.elements.draftElementId;
    const {
      activeCurrency,
      activeLanguage,
      moneyFormat,
      moneyWithCurrencyFormat,
    } = selectLocaleData(state);
    const templateProduct = selectTemplateEditorStoreProduct(state) ?? null;
    const draftElement = getFromRecordOrNull(
      state.core.elements.mapping,
      draftElementId,
    );
    const projectId = selectProjectId(state);
    if (!draftElement || !projectId) {
      return;
    }
    const { dependencies: dependenciesRecord } = calculateDependencies(
      draftElement.component,
      {
        productResolutionDependencies: {
          products: recordValues(selectProductData(state)),
          currencyCode: activeCurrency,
          language: activeLanguage,
          moneyFormat,
          moneyWithCurrencyFormat,
          templateProduct,
          isEditor: true,
          isShopifyProductsLoading: false,
        },
        // Note (Noah, 2022-11-26): Okay to pass empty mapping here because we only
        // care about the metafield keys/namespaces, not the types of the metafields
        // here
        metafieldsNamespaceKeyTypeMapping: { product: {}, variant: {} },
        templateProductId: templateProduct?.id ?? null,
      },
    );

    // Group dependencies by product id so that we can fetch all the necessary
    // metafields' values from different namespaces in a single request.
    // @ts-ignore
    const productIdToMetafieldNamespacesAndKeys: Record<
      ProductId,
      Record<MetafieldNamespace, MetafieldKey[]>
    > = mapValues(
      groupBy(
        Object.values(dependenciesRecord)
          .flat()
          .filter((dependency) => {
            return (
              dependency.type === "metafields" &&
              dependency.entityType === "product"
            );
          }),
        "productId",
      ),
      (dependencies: ProductMetafieldsDependency[]) => {
        const uniqueNamespaces = uniq(
          // Extract all needed namespaces from the dependencies so that
          // we only fetch the necessary metafields' values.
          dependencies.flatMap((dependency) => {
            return (
              dependency as ProductMetafieldsDependency
            ).metafieldKeys.map(({ namespace }) => namespace);
          }),
        );
        const namespaceToKeys: Record<MetafieldNamespace, MetafieldKey[]> = {};
        for (const dependency of dependencies) {
          for (const namespace of uniqueNamespaces) {
            if (!namespaceToKeys[namespace]) {
              namespaceToKeys[namespace] = [];
            }
            uniq(
              dependency.metafieldKeys
                .filter((key) => key.namespace === namespace)
                .map(({ key }) => key),
            ).forEach((key) => {
              if (!namespaceToKeys[namespace]?.find((k) => key === k)) {
                namespaceToKeys[namespace]?.push(key);
              }
            });
          }
        }
        return namespaceToKeys;
      },
    );

    if (size(productIdToMetafieldNamespacesAndKeys) > 0) {
      try {
        const response = await shopifyClient.getProductMetafieldValues.mutate({
          projectId,
          productIdsWithNamespaces: productIdToMetafieldNamespacesAndKeys,
        });

        const { metafields: productsMetafieldValues } = response;
        if (
          productsMetafieldValues &&
          Object.keys(productsMetafieldValues).length > 0
        ) {
          dispatch(setProductMetafieldValues(productsMetafieldValues));
        }
      } catch (error) {
        return rejectWithValue(error);
      }
    }

    // @ts-ignore
    const variantIdToMetafieldNamespacesAndKeys: Record<
      VariantId,
      Record<MetafieldNamespace, MetafieldKey[]>
    > = mapValues(
      groupBy(
        Object.values(dependenciesRecord)
          .flat()
          .filter((dependency) => {
            return (
              dependency.type === "metafields" &&
              dependency.entityType === "variant"
            );
          }),
        "variantId",
      ),
      (dependencies: VariantMetafieldsDependency[]) => {
        const uniqueNamespaces = uniq(
          // Extract all neeeded namespaces from the dependencies so that
          // we only fetch the necessary metafields' values.
          dependencies.flatMap((dependency) => {
            return (
              dependency as VariantMetafieldsDependency
            ).metafieldKeys.map(({ namespace }) => namespace);
          }),
        );
        const namespaceToKeys: Record<MetafieldNamespace, MetafieldKey[]> = {};
        for (const dependency of dependencies) {
          for (const namespace of uniqueNamespaces) {
            if (!namespaceToKeys[namespace]) {
              namespaceToKeys[namespace] = [];
            }
            uniq(
              dependency.metafieldKeys
                .filter((key) => key.namespace === namespace)
                .map(({ key }) => key),
            ).forEach((key) => {
              if (!namespaceToKeys[namespace]?.find((k) => key === k)) {
                namespaceToKeys[namespace]?.push(key);
              }
            });
          }
        }
        return namespaceToKeys;
      },
    );
    if (size(variantIdToMetafieldNamespacesAndKeys) > 0) {
      try {
        const response = await shopifyClient.getVariantMetafieldValues.mutate({
          projectId,
          variantIdsWithNamespaces: variantIdToMetafieldNamespacesAndKeys,
        });
        const { metafields: variantsMetafieldValues } = response;

        if (
          variantsMetafieldValues &&
          Object.keys(variantsMetafieldValues).length > 0
        ) {
          dispatch(setVariantsMetafieldValues(variantsMetafieldValues));
        }
      } catch (error) {
        return rejectWithValue(error);
      }
    }
  },
);

export const applyComponentAction = (
  payload: ComponentActionType,
): AnyAction => {
  // @ts-ignore
  return [
    createElementUpdatingThunk(applyComponentRTKAction(payload), (getState) => {
      return payload.elementId ?? getState().core.elements.draftElementId;
    }),
    createFetchMetafieldValuesIfNeededThunk(payload.elementId ?? null),
  ];
};

export const updateElement = (
  elementId: string,
  payload: ReploElement | ReploPartialElement,
) => {
  return {
    type: EditorActionType.UPDATE_ELEMENT,
    payload: {
      ...payload,
      id: elementId,
    },
  };
};

const savePendingElements = (source: UpdateElementSource) => {
  return updateComponent(source);
};

const updateComponent = (source: UpdateElementSource) => {
  return async (dispatch: EditorDispatch, getState: () => EditorRootState) => {
    const coreState = getState().core;
    const pendingElementIds = coreState.pendingElementUpdates;
    let elementIdToUpdate: string | null;

    if (pendingElementIds.length === 0) {
      elementIdToUpdate = coreState.elements.draftElementId;
      if (!elementIdToUpdate) {
        return;
      }
    } else {
      elementIdToUpdate = pendingElementIds[0]!;
    }

    const elementToUpdate = getFromRecordOrNull(
      coreState.elements.mapping,
      elementIdToUpdate,
    );
    if (!elementToUpdate) {
      console.error(`Element ${elementIdToUpdate} not found for update`);
      return;
    }

    const elementToUpdateVersion = getFromRecordOrNull(
      coreState.elements.versionMapping,
      elementIdToUpdate,
    );
    if (elementToUpdateVersion == null) {
      // eslint-disable-next-line replo/no-raw-error-throw
      throw new Error("Element version not found");
    }

    const bodyComponent = {
      id: elementIdToUpdate,
      component: elementToUpdate.component,
      version: elementToUpdateVersion,
    };

    try {
      dispatch(handleUpdateComponentStart({ elementId: elementIdToUpdate }));

      await elementClient.updateComponent.mutate({
        component: bodyComponent,
      });

      dispatch(handleUpdateComponentSuccess({ elementId: elementIdToUpdate }));

      if (source === "pageSettings") {
        modalStore.getState().actions.clearAllModals();
      }
    } catch (error) {
      trackError(error, {
        message: "Failed to update element",
        elementId: elementIdToUpdate,
      });
      const { element: latestElementData } =
        await elementClient.getById.query(elementIdToUpdate);
      dispatch(
        handleUpdateComponentError({
          elementId: elementIdToUpdate,
          error,
          latestElementData,
        }),
      );
    } finally {
      const updatedCoreState = getState().core;
      if (updatedCoreState.pendingElementUpdates.length > 0) {
        void dispatch(savePendingElements(source));
      }
    }
  };
};

export const getDependencyData = (state: EditorRootState) => {
  const draftElementId = state.core.elements.draftElementId;
  const productDataFromStore = selectProductData(state);
  const productMetafieldValues = state.commerce.productMetafieldValues;
  const variantMetafieldValues = state.commerce.variantMetafieldValues;
  const templateProduct = selectTemplateEditorStoreProduct(state) ?? null;
  const {
    activeCurrency,
    activeLanguage,
    moneyFormat,
    moneyWithCurrencyFormat,
  } = selectLocaleData(state);

  const draftElement = getFromRecordOrNull(
    state.core.elements.mapping,
    draftElementId,
  );

  const { dependencies, allReferencedMetafields } = draftElement
    ? calculateDependencies(draftElement.component, {
        productResolutionDependencies: {
          products: Object.values(productDataFromStore),
          currencyCode: activeCurrency,
          language: activeLanguage,
          moneyFormat,
          moneyWithCurrencyFormat,
          templateProduct,
          isEditor: true,
          isShopifyProductsLoading: false,
        },
        metafieldsNamespaceKeyTypeMapping: {
          // NOTE (Gabe 2024-09-18): This function ends up producing a mapping
          // that only contains keys of metafields that are present in products
          // in redux. For product templates it's possible that a metafield is
          // referenced, but not present in a product loaded into redux during
          // dependency generation. For that reason we obtain the types on the
          // backend from the Shopify API for Template Products.
          product: getMetafieldsNamespaceKeyTypeMapping(productMetafieldValues),
          variant: getMetafieldsNamespaceKeyTypeMapping(variantMetafieldValues),
        },
        templateProductId: templateProduct?.id ?? null,
      })
    : {
        dependencies: {},
        allReferencedMetafields: {
          product: [],
          variant: [],
        },
      };

  const productsData = getProductsDataForDependencies(
    dependencies,
    productDataFromStore,
    templateProduct?.id ?? null,
  );

  const useShopifyDebugTools = Boolean(
    JSON.parse(
      localStorage.getItem("replo.debug.useShopifyDebugTools") ?? "false",
    ),
  );

  return {
    elementId: draftElementId,
    dependencies,
    products: productsData,
    variantMetafieldValues,
    allReferencedMetafields,
    productMetafieldValues,
    theme_id: state.ui.themeId,
    okendoNamespace: state.commerce.okendoWidgetNamespace,
    useShopifyDebugTools,
  };
};

type CreatePreviewDataProps = {
  state: EditorRootState;
  projectId: string;
  storeUrl: string;
};

export const createPreviewData = (
  props: CreatePreviewDataProps,
): PreviewElementProps | undefined => {
  const { state, projectId, storeUrl } = props;
  const draftElementId = selectDraftElementId(state);
  const {
    activeCurrency,
    activeLanguage,
    moneyFormat,
    moneyWithCurrencyFormat,
  } = selectLocaleData(state);
  const activeShopifyUrlRoot = selectShopifyUrlRoot(state);
  const draftElementVersion = selectDraftElementVersion(state);

  if (
    !draftElementId ||
    !projectId ||
    !storeUrl ||
    draftElementVersion == null
  ) {
    return;
  }

  const publishData = getDependencyData(state);

  if (publishData.elementId === null) {
    return;
  }

  return {
    ...publishData,
    elementId: publishData.elementId,
    projectId,
    previewElementVersion: draftElementVersion,
    storeUrl,
    elementProducts: publishData.products,
    currencyCode: activeCurrency,
    moneyFormat,
    moneyWithCurrencyFormat,
    activeLanguage,
    activeShopifyUrlRoot,
  };
};

// NOTE (Fran 2024-01-15): This is deprecated, we will remove it once we migrate this action to RTK
export type UpdateEditorAction = {
  type: "UPDATE_ELEMENT";
  payload: ReploElement | (ReploPartialElement & { id: ReploElement["id"] });
};
